<template>
  <v-container fluid class="pa-0 pa-md-4 py-12">
    <v-btn variant="text" prepend-icon="mdi-arrow-left" class="mb-4" @click="goBack">
      {{ backLabel }}
    </v-btn>

    <v-card elevation="1" class="pa-6 vehicle-details-card">
      <v-card-title class="text-h5 font-weight-bold">Vehicle Details</v-card-title>

      <div class="vehicle-history-corner">
        <v-tooltip location="bottom" text="View history">
          <template #activator="{ props }">
            <v-btn
              v-show="!historyDialogOpen && !isUnpublishedCreateDraft"
              v-bind="props"
              prepend-icon="mdi-history"
              variant="text"
              size="small"
              color="grey-darken-1"
              class="text-none"
              :loading="historyLoading"
              :disabled="!vehicle || isUnpublishedCreateDraft"
              aria-label="View vehicle history"
              @click="openHistoryDialog"
            >
              View history
            </v-btn>
          </template>
        </v-tooltip>
      </div>

      <div class="vehicle-details-vin-row d-flex align-center justify-space-between flex-wrap ga-3 mb-2">
        <div class="text-subtitle-1 text-medium-emphasis">VIN: {{ vin }}</div>
        <div class="vehicle-details-actions d-flex align-center flex-wrap ga-2 justify-end">
        <v-btn
          v-if="canRestoreArchivedVehicle"
          color="primary"
          variant="tonal"
          prepend-icon="mdi-backup-restore"
          :loading="isRestoring"
          @click="openRestoreDialog"
        >
          Restore Vehicle
        </v-btn>
        <v-btn
          v-if="canPurgeRemovedFeedVin"
          color="error"
          variant="tonal"
          prepend-icon="mdi-delete-forever-outline"
          :loading="isPurgingRemoved"
          @click="purgeRemovedDialog = true"
        >
          Delete Record
        </v-btn>
        <v-btn
          v-if="canPurgeRemovedManualVin"
          color="error"
          variant="tonal"
          prepend-icon="mdi-delete-forever-outline"
          :loading="isPurgingManual"
          @click="purgeManualDialog = true"
        >
          Delete Record
        </v-btn>
        <v-btn
          v-if="canEditVehicle"
          color="primary"
          variant="tonal"
          prepend-icon="mdi-pencil"
          @click="goToEdit"
        >
          Edit Vehicle
        </v-btn>
        <v-btn
          v-if="shouldShowDeleteDraftAction"
          color="error"
          variant="outlined"
          prepend-icon="mdi-delete-outline"
          :loading="isDeletingDraft"
          @click="deleteDraftDialogOpen = true"
        >
          Delete draft
        </v-btn>
        <v-btn
          v-if="shouldShowArchiveVehicleAction"
          color="error"
          variant="outlined"
          prepend-icon="mdi-trash-can-outline"
          :loading="isRemoving"
          :disabled="disableRemove"
          :title="disableRemoveReason"
          @click="removeDialog = true"
        >
          Archive Vehicle
        </v-btn>
        </div>
      </div>

      <v-alert
        v-if="vehicleDetailsNotice"
        :type="vehicleDetailsNotice.type"
        variant="tonal"
        density="comfortable"
        class="mt-4 mb-4"
      >
        <div class="d-flex align-center justify-space-between flex-wrap ga-2">
          <div>
            <div>{{ vehicleDetailsNotice.text }}</div>
            <div
              v-if="hasPendingRestoreDraft && restoreStagedSummary"
              class="text-caption mt-1"
            >
              {{ restoreStagedSummary }}
            </div>
          </div>
          <v-btn
            v-if="shouldShowUnpublishedChangesAction"
            size="small"
            variant="tonal"
            color="primary"
            class="text-none"
            @click="goToUnpublishedChanges"
          >
            View unpublished changes
          </v-btn>
          <v-btn
            v-if="shouldShowRevertArchiveInNotice"
            size="small"
            variant="tonal"
            color="warning"
            class="text-none"
            prepend-icon="mdi-undo"
            :loading="isCancelingDelete"
            :disabled="isCancelingDelete || isRemoving"
            @click="handleRevertStagedDelete"
          >
            Revert archive
          </v-btn>
          <v-btn
            v-if="shouldShowRevertRestoreInNotice"
            size="small"
            variant="tonal"
            color="primary"
            class="text-none"
            prepend-icon="mdi-undo"
            :loading="isRevertingRestore"
            :disabled="isRevertingRestore || isRestoring"
            @click="handleRevertRestore"
          >
            Revert restore
          </v-btn>
        </div>
      </v-alert>

      <v-alert v-if="detailsLoadError" type="error" variant="tonal" density="comfortable" class="mt-4 mb-4">
        {{ detailsLoadError }}
      </v-alert>

      <v-row v-if="vehicle && !isUnpublishedCreateDraft" class="mb-4 mt-2">
        <v-col cols="12">
          <v-card variant="outlined" class="pa-4">
            <div class="text-subtitle-2 font-weight-bold mb-3">Status summary</div>
            <div class="status-summary-grid">
              <div class="status-summary-item">
                <div class="text-caption text-grey">Inventory status</div>
                <v-chip size="small" variant="tonal" color="primary">
                  {{ inventoryStatusLabel }}
                </v-chip>
              </div>

              <div class="status-summary-item">
                <div class="text-caption text-grey">Website visibility</div>
                <div v-if="!canShowWebsiteVisibilityControls" class="mt-1">
                  <v-chip size="small" variant="tonal" color="grey">
                    {{ websiteStatusLabel }}
                  </v-chip>
                </div>
                <div v-else class="website-visibility-summary mt-1">
                  <div class="text-body-2 font-weight-medium">{{ websiteStatusLabel }}</div>
                  <div class="text-caption text-medium-emphasis">
                    {{ websiteVisibilityModeLabel }}
                  </div>
                  <div class="d-flex flex-wrap ga-2 mt-2">
                    <v-btn
                      v-if="showShowOnWebsiteAction"
                      color="success"
                      variant="tonal"
                      size="small"
                      prepend-icon="mdi-eye-outline"
                      class="text-none"
                      :loading="isTogglingWebsiteVisibility"
                      @click="handleWebsiteVisibilityToggle(true)"
                    >
                      Show on website
                    </v-btn>
                    <v-btn
                      v-if="showHideFromWebsiteAction"
                      color="warning"
                      variant="tonal"
                      size="small"
                      prepend-icon="mdi-eye-off-outline"
                      class="text-none"
                      :loading="isTogglingWebsiteVisibility"
                      @click="handleWebsiteVisibilityToggle(false)"
                    >
                      Hide from website
                    </v-btn>
                    <v-btn
                      v-if="showUseAutomaticVisibilityAction"
                      color="primary"
                      variant="text"
                      size="small"
                      prepend-icon="mdi-auto-fix"
                      class="text-none"
                      :loading="isTogglingWebsiteVisibility"
                      @click="handleReturnToAutomaticVisibility"
                    >
                      Use automatic visibility
                    </v-btn>
                  </div>
                </div>
              </div>

              <div class="status-summary-item">
                <div class="text-caption text-grey">Source</div>
                <v-chip size="small" variant="tonal" :color="isManualVehicle ? 'deep-purple' : 'primary'">
                  {{ sourceLabel }}
                </v-chip>
              </div>

              <div v-if="isManualVehicle" class="status-summary-item">
                <div class="text-caption text-grey">Feed reclaim</div>
                <v-switch
                  :model-value="allowFeedReclaim"
                  color="primary"
                  density="compact"
                  hide-details
                  :loading="isTogglingAllowFeedReclaim"
                  :disabled="isTogglingAllowFeedReclaim"
                  :label="allowFeedReclaim ? 'Allowed' : 'Not allowed'"
                  class="mt-1"
                  @update:model-value="handleToggleAllowFeedReclaim"
                />
                <div class="text-caption text-medium-emphasis">
                  When on, a feed that later includes this VIN can reclaim it back to feed-backed.
                </div>
              </div>

              <div v-if="isArchivedVehicle" class="status-summary-item">
                <div class="text-caption text-grey">Archive reason</div>
                <div class="text-body-2">{{ archiveReasonLabel }}</div>
              </div>

              <div class="status-summary-item">
                <div class="text-caption text-grey">Created</div>
                <DateTimeWithDealerTooltip
                  v-if="createdDate"
                  :value="createdDate"
                  :text="formatISODate(createdDate)"
                />
                <template v-else>—</template>
              </div>

              <div v-if="showArchivedDateInStatusSummary" class="status-summary-item">
                <div class="text-caption text-grey">Archived</div>
                <DateTimeWithDealerTooltip
                  v-if="archivedDate"
                  :value="archivedDate"
                  :text="formatISODate(archivedDate)"
                />
                <template v-else>—</template>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-card-text v-if="vehicle">
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <strong>Year</strong>
            <div>{{ view.year ?? '—' }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <strong>Make</strong>
            <div>{{ view.make ?? '—' }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <strong>Model</strong>
            <div>{{ view.model ?? '—' }}</div>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <strong>Trim</strong>
            <div>{{ view.trim ?? '—' }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <strong>Stock #</strong>
            <div>{{ view.stock ?? '—' }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <strong>Condition</strong>
            <div>{{ view.condition ?? '—' }}</div>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <strong>Price</strong>
            <div v-if="priceInspectorUrl" class="d-flex align-center">
              <div data-testid="vehicle-details-price-value">
                {{ view.price != null ? formatPrice(view.price) : '—' }}
              </div>
              <PriceInspectorLink
                :href="priceInspectorUrl"
                :vin="vin"
                test-id="vehicle-details-price-inspector-link"
              />
            </div>
            <div v-else data-testid="vehicle-details-price-value">
              {{ view.price != null ? formatPrice(view.price) : '—' }}
            </div>
            <div
              v-if="hasActivePriceOverride"
              class="text-caption text-warning mt-1"
              data-testid="vehicle-details-price-active-override"
            >
              Manual override active
            </div>
          </v-col>
        </v-row>

        <v-dialog v-model="historyDialogOpen" content-class="vehicle-history-dialog">
          <v-card class="vehicle-history-card">
            <v-card-title class="d-flex align-center justify-space-between">
              <span>Vehicle History</span>
              <v-btn
                icon="mdi-close"
                variant="text"
                aria-label="Close history"
                @click="historyDialogOpen = false"
              />
            </v-card-title>
            <v-progress-linear v-if="historyLoading" indeterminate color="primary" />
            <v-card-text class="vehicle-history-body">
              <v-alert v-if="historyError" type="error" variant="tonal" density="comfortable">
                {{ historyError }}
              </v-alert>
              <template v-else>
                <v-alert
                  v-if="historyTruncated"
                  type="warning"
                  variant="tonal"
                  density="comfortable"
                  class="mb-3"
                >
                  History is truncated (safety cap).
                </v-alert>
                <div v-if="historyItems.length === 0" class="text-body-2 text-grey">
                  No history available.
                </div>
                <!-- Newest first. Field-change events are accordions (closed by default); one-liners are static rows (R17.3). -->
                <div v-else class="vehicle-history-list">
                  <template v-for="{ row, display } in historyDisplayItems" :key="row.id">
                    <v-expansion-panels
                      v-if="display.changes?.length"
                      variant="accordion"
                      flat
                      class="vehicle-history-item"
                      data-testid="vehicle-history-accordion"
                    >
                      <v-expansion-panel>
                        <v-expansion-panel-title>
                          <span class="vehicle-history-item__header">
                            <span class="vehicle-history-item__date">
                              <span class="text-body-2">
                                <DateTimeWithDealerTooltip :value="row.createdAt" :text="display.dateTop" />
                              </span>
                              <span class="text-caption text-grey ml-2">
                                <DateTimeWithDealerTooltip :value="row.createdAt" kind="time" :text="display.timeBottom" />
                              </span>
                            </span>
                            <span class="vehicle-history-item__sep" aria-hidden="true"></span>
                            <span class="vehicle-history-item__labeled">
                              <span class="text-body-2 text-medium-emphasis">Operation:</span>
                              <v-chip size="x-small" :color="historyEventColor(row.eventType)" variant="tonal">
                                {{ display.operationShort }}
                              </v-chip>
                            </span>
                            <span class="vehicle-history-item__sep" aria-hidden="true"></span>
                            <span class="vehicle-history-item__labeled">
                              <span class="text-body-2 text-medium-emphasis">Source:</span>
                              <v-chip size="x-small" variant="tonal" color="grey-darken-1" class="vehicle-history-item__source-chip">
                                {{ display.by }}
                              </v-chip>
                            </span>
                            <span class="vehicle-history-item__sep" aria-hidden="true"></span>
                            <span class="vehicle-history-item__summary vehicle-history-item__labeled">
                              <span class="text-body-2 text-medium-emphasis">Fields:</span>
                              <span class="text-body-2">{{ summarizeHistoryFieldNames(display.changes) }}</span>
                            </span>
                          </span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <v-table density="compact" class="vehicle-details-history-changes-table">
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th>Before</th>
                                <th>After</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="c in display.changes" :key="`${row.id}_${c.field}`">
                                <td class="text-caption">{{ c.field }}</td>
                                <td class="text-caption"><div class="vehicle-details-history-value">{{ c.before }}</div></td>
                                <td class="text-caption"><div class="vehicle-details-history-value">{{ c.after }}</div></td>
                              </tr>
                            </tbody>
                          </v-table>
                          <div v-if="row.correlationId" class="text-caption vehicle-details-history-correlation mt-2">
                            <v-tooltip location="top" text="Copy correlation ID">
                              <template #activator="{ props }">
                                <v-btn
                                  v-bind="props"
                                  icon="mdi-content-copy"
                                  variant="text"
                                  size="x-small"
                                  class="mr-1"
                                  aria-label="Copy correlation ID"
                                  @click.stop="copyToClipboard(row.correlationId)"
                                />
                              </template>
                            </v-tooltip>
                            <v-tooltip location="top" text="View publish logs">
                              <template #activator="{ props }">
                                <v-btn
                                  v-bind="props"
                                  icon="mdi-text-box-search-outline"
                                  variant="text"
                                  size="x-small"
                                  class="mr-1"
                                  aria-label="View publish logs"
                                  @click.stop="openPublishLogsForCorrelationId(row.correlationId)"
                                />
                              </template>
                            </v-tooltip>
                            <span>{{ row.correlationId }}</span>
                          </div>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>
                    <div v-else class="vehicle-history-item vehicle-history-item--static" data-testid="vehicle-history-row">
                      <div class="vehicle-history-item__header">
                        <span class="vehicle-history-item__date">
                          <span class="text-body-2">
                            <DateTimeWithDealerTooltip :value="row.createdAt" :text="display.dateTop" />
                          </span>
                          <span class="text-caption text-grey ml-2">
                            <DateTimeWithDealerTooltip :value="row.createdAt" kind="time" :text="display.timeBottom" />
                          </span>
                        </span>
                        <span class="vehicle-history-item__sep" aria-hidden="true"></span>
                        <span class="vehicle-history-item__labeled">
                          <span class="text-body-2 text-medium-emphasis">Operation:</span>
                          <v-chip size="x-small" :color="historyEventColor(row.eventType)" variant="tonal">
                            {{ display.operationShort }}
                          </v-chip>
                        </span>
                        <span class="vehicle-history-item__sep" aria-hidden="true"></span>
                        <span class="vehicle-history-item__labeled">
                          <span class="text-body-2 text-medium-emphasis">Source:</span>
                          <v-chip size="x-small" variant="tonal" color="grey-darken-1" class="vehicle-history-item__source-chip">
                            {{ display.by }}
                          </v-chip>
                        </span>
                        <span class="vehicle-history-item__sep" aria-hidden="true"></span>
                        <div class="vehicle-history-item__summary vehicle-history-item__labeled vehicle-history-item__labeled--top">
                          <span class="text-body-2 text-medium-emphasis">Changes:</span>
                          <div class="vehicle-details-history-lines vehicle-history-item__changes text-body-2">
                            <template v-if="display.changed?.length">
                              <template v-for="(line, idx) in display.changed" :key="idx">
                                <span v-if="idx > 0" class="vehicle-history-item__sep" aria-hidden="true"></span>
                                <span class="vehicle-history-item__change">
                                  <span
                                    v-for="(part, partIdx) in historyChangeParts(row, line)"
                                    :key="partIdx"
                                    :class="part.tone ? `vehicle-history-item__visibility--${part.tone}` : undefined"
                                  >{{ part.text }}</span>
                                </span>
                              </template>
                            </template>
                            <template v-else>—</template>
                          </div>
                        </div>
                        <div v-if="row.correlationId" class="text-caption vehicle-details-history-correlation">
                          <v-tooltip location="top" text="Copy correlation ID">
                            <template #activator="{ props }">
                              <v-btn
                                v-bind="props"
                                icon="mdi-content-copy"
                                variant="text"
                                size="x-small"
                                class="mr-1"
                                aria-label="Copy correlation ID"
                                @click.stop="copyToClipboard(row.correlationId)"
                              />
                            </template>
                          </v-tooltip>
                          <v-tooltip location="top" text="View publish logs">
                            <template #activator="{ props }">
                              <v-btn
                                v-bind="props"
                                icon="mdi-text-box-search-outline"
                                variant="text"
                                size="x-small"
                                class="mr-1"
                                aria-label="View publish logs"
                                @click.stop="openPublishLogsForCorrelationId(row.correlationId)"
                              />
                            </template>
                          </v-tooltip>
                          <span>{{ row.correlationId }}</span>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-dialog>

        <PublishLogsDialog
          v-model="publishLogsDialogOpen"
          :endpoint="endpoint"
          :jazel-id="accountSettings.accountId"
          :correlation-id="publishLogsCorrelationId"
          :session-token="accountSettings.sessionToken"
          :max-lines="200"
        />

        <!-- Tags — editable for current vehicles only -->
        <v-row
          v-if="!isUnpublishedCreateDraft && !isArchivedVehicle && (membershipStatus === 'CURRENT' || vehicle)"
          class="mb-4"
        >
          <v-col cols="12">
            <v-card variant="outlined">
              <v-card-text class="pa-3">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div>
                    <div class="text-subtitle-2 font-weight-medium">Tags</div>
                    <div class="text-caption text-grey">
                      Add labels to merchandise or track this vehicle. Changes take effect immediately.
                    </div>
                  </div>
                  <v-btn
                    v-if="!isEditingTags"
                    size="small"
                    variant="text"
                    prepend-icon="mdi-pencil"
                    @click="startEditingTags"
                  >
                    Edit
                  </v-btn>
                </div>

                <!-- View mode: show chips -->
                <div v-if="!isEditingTags">
                  <div v-if="hasAnyVehicleTags" class="d-flex flex-wrap ga-1">
                    <VehicleTagChips :tags="vehicleTags" :system-tags="vehicleSystemTags" size="small" />
                  </div>
                  <div v-else class="text-caption text-grey-darken-1 font-italic">
                    No tags set.
                  </div>
                </div>

                <!-- Edit mode: multi-select picker -->
                <div v-else>
                  <v-select
                    v-model="editingTags"
                    :items="USER_TAG_OPTIONS_FOR_SELECT"
                    item-title="label"
                    item-value="value"
                    label="User tags"
                    variant="outlined"
                    density="comfortable"
                    multiple
                    chips
                    closable-chips
                    hide-details
                    class="mb-3"
                  />

                  <!-- System tags read-only display -->
                  <div v-if="vehicleSystemTags.length" class="mb-3">
                    <div class="text-caption text-grey mb-1">
                      <v-icon size="14" class="mr-1">mdi-lightning-bolt</v-icon>
                      System tags (auto-computed, read-only)
                    </div>
                    <VehicleTagChips :system-tags="vehicleSystemTags" size="small" />
                  </div>

                  <div class="d-flex align-center ga-2">
                    <v-btn
                      color="primary"
                      variant="flat"
                      size="small"
                      :loading="isSavingTags"
                      @click="saveTagEdits"
                    >
                      Save Tags
                    </v-btn>
                    <v-btn
                      variant="text"
                      size="small"
                      :disabled="isSavingTags"
                      @click="cancelTagEdits"
                    >
                      Cancel
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tags — read-only snapshot for archived vehicles -->
        <v-row v-else-if="isArchivedVehicle && hasAnyVehicleTags" class="mb-4">
          <v-col cols="12">
            <v-card variant="outlined">
              <v-card-text class="pa-3">
                <div class="text-subtitle-2 font-weight-medium mb-1">Tags</div>
                <div class="text-caption text-grey mb-2">
                  Labels retained from when this vehicle was current inventory.
                </div>
                <VehicleTagChips :tags="vehicleTags" :system-tags="vehicleSystemTags" size="small" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Photos -->
        <v-row v-if="view.photos?.length" class="mb-6">
          <v-col cols="12">
            <v-divider class="my-2" />
            <div class="text-subtitle-1 font-weight-bold text-primary mt-4 mb-2">Photos</div>
          </v-col>
          <v-col cols="12">
            <div class="vehicle-details-photo-carousel-wrap">
              <v-carousel
                v-model="photoCarouselIndex"
                :cycle="false"
                :continuous="false"
                :show-arrows="photoPages.length > 1"
                hide-delimiters
                height="auto"
                class="vehicle-details-photo-carousel"
              >
                <v-carousel-item v-for="(page, pageIdx) in photoPages" :key="pageIdx">
                  <v-row>
                    <v-col v-for="(url, idx) in page" :key="idx" cols="6" md="3">
                      <v-img
                        :src="url"
                        :alt="`Photo ${pageIdx * 4 + idx + 1}`"
                        cover
                        aspect-ratio="4/3"
                        class="rounded-lg border vehicle-details-photo-thumb"
                        role="button"
                        tabindex="0"
                        @click="openPhotoPreview(pageIdx * 4 + idx)"
                        @keydown.enter="openPhotoPreview(pageIdx * 4 + idx)"
                      />
                    </v-col>
                  </v-row>
                </v-carousel-item>
              </v-carousel>

              <div
                v-if="photoPages.length > 1"
                class="vehicle-details-photo-carousel-fraction"
                aria-label="Photo carousel position"
              >
                {{ photoCarouselIndex + 1 }} / {{ photoPages.length }}
              </div>
            </div>
          </v-col>
        </v-row>

        <v-dialog v-model="photoPreviewOpen" content-class="photo-preview-dialog">
          <v-card class="photo-preview-card">
            <v-btn
              icon="mdi-close"
              variant="flat"
              size="large"
              color="surface"
              class="photo-preview-close"
              aria-label="Close photo preview"
              @click="photoPreviewOpen = false"
            />
            <v-card-text class="photo-preview-body">
              <div class="photo-preview-wrap">
                <v-carousel
                  v-if="photoPreviewSlides.length > 0"
                  v-model="photoPreviewIndex"
                  :cycle="false"
                  :continuous="false"
                  :show-arrows="photoPreviewSlides.length > 1"
                  hide-delimiters
                  height="100%"
                >
                  <v-carousel-item v-for="(src, i) in photoPreviewSlides" :key="`${src}_${i}`">
                    <div class="photo-preview-stage">
                      <img :src="src" alt="Photo preview" class="photo-preview-img rounded" />
                    </div>
                  </v-carousel-item>
                </v-carousel>

                <div
                  v-if="photoPreviewSlides.length > 1"
                  class="photo-preview-fraction"
                  aria-label="Photo preview position"
                >
                  {{ photoPreviewIndex + 1 }} / {{ photoPreviewSlides.length }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <v-row v-if="isArchivedVehicle && fieldSections.length > 0" class="mb-2">
          <v-col cols="12">
            <div class="text-h6 font-weight-bold">Vehicle snapshot</div>
            <div class="text-caption text-grey">
              Read-only values from when this vehicle was archived.
            </div>
          </v-col>
        </v-row>

        <!-- Dynamic fields organized by section (matching AddVehicle) -->
        <template v-for="(section, sectionIndex) in fieldSections" :key="sectionIndex">
          <v-row v-if="section.fields.length > 0" class="mb-4">
            <v-col cols="12">
              <v-divider class="my-2" />
              <div class="text-subtitle-1 font-weight-bold text-primary mt-4 mb-2">
                {{ section.label }}
              </div>
            </v-col>
            <v-col
              v-for="field in section.fields"
              :key="field"
              cols="12"
              :md="isFullWidthDetailField(section.label, field) ? 12 : 4"
            >
              <div class="text-caption text-grey">{{ formatFieldLabel(field) }}</div>
              <div
                class="text-body-2"
                :class="{
                  'vehicle-details-content-field': isFullWidthDetailField(section.label, field),
                  'vehicle-details-tech-specs': isStructuredSpecField(field),
                }"
              >
                <DateTimeWithDealerTooltip
                  v-if="field === 'date_in_stock_ts'"
                  :value="dateInStockToInstantMs(getVehicleDetailFieldValue(field))"
                  :text="formatDealerDateOnly(getVehicleDetailFieldValue(field), dealerTimeZone)"
                />
                <template v-else-if="isFeatureField(field)">
                  {{ formatFeatureFieldValue(getVehicleDetailFieldValue(field)) }}
                </template>
                <template v-else>
                  {{ formatFieldValue(getVehicleDetailFieldValue(field), field) }}
                </template>
              </div>
            </v-col>
          </v-row>
        </template>

        <v-row v-if="view.comments" class="mb-4">
          <v-col cols="12">
            <v-divider class="my-2" />
            <div class="text-subtitle-1 font-weight-bold text-primary mt-4 mb-2">Comments</div>
          </v-col>
          <v-col cols="12">
            <div class="text-caption text-grey">{{ formatFieldLabel('dealer_comments') }}</div>
            <div class="text-body-2 vehicle-details-content-field" v-html="sanitizedComments"></div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Confirm Restore (removed VINs only) -->
    <v-dialog v-model="restoreDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">Restore vehicle?</v-card-title>
        <v-card-text class="d-flex flex-column ga-4">
          <div>
            This will stage this vehicle to return to current inventory when changes are published.
          </div>
          <v-select
            v-model="restoreInventoryState"
            :items="restoreInventoryStateOptions"
            item-title="title"
            item-value="value"
            label="Inventory status"
            variant="outlined"
            density="comfortable"
            hide-details
          />
          <v-select
            v-model="restoreInventoryUsageType"
            :items="restoreInventoryUsageTypeOptions"
            item-title="title"
            item-value="value"
            label="Usage type"
            variant="outlined"
            density="comfortable"
            hide-details
          />
          <v-select
            v-model="restoreWebsiteVisibilityChoice"
            :items="restoreWebsiteVisibilityOptions"
            item-title="title"
            item-value="value"
            label="Website visibility"
            variant="outlined"
            density="comfortable"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isRestoring" @click="restoreDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="isRestoring" @click="handleRestore">
            Stage restore
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm purge removed feed VIN record -->
    <v-dialog v-model="purgeRemovedDialog" max-width="560">
      <v-card>
        <v-card-title class="text-h6">Permanently delete this record?</v-card-title>
        <v-card-text>
          This will permanently purge all stored history for VIN <strong>{{ vin }}</strong> for this dealer
          (including audit history and drafts). This cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isPurgingRemoved" @click="purgeRemovedDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="isPurgingRemoved" @click="handlePurgeRemovedFeedVin">
            Delete Record
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm hard delete manual VIN record (purges SRP + artifacts) -->
    <v-dialog v-model="purgeManualDialog" max-width="560">
      <v-card>
        <v-card-title class="text-h6">Permanently delete this record?</v-card-title>
        <v-card-text>
          This will permanently hard-delete VIN <strong>{{ vin }}</strong> for this dealer (including the SRP object and
          stored artifacts in S3). This cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isPurgingManual" @click="purgeManualDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="isPurgingManual" @click="handlePurgeRemovedManualVin">
            Delete Record
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm delete CREATE draft -->
    <v-dialog v-model="deleteDraftDialogOpen" max-width="520">
      <v-card>
        <v-card-title>Delete draft vehicle?</v-card-title>
        <v-card-text>
          This will discard this unpublished manual vehicle. It has not been published yet and will not appear in current inventory.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isDeletingDraft" @click="deleteDraftDialogOpen = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="isDeletingDraft"
            @click="handleDeleteCreateDraft"
          >
            Delete draft
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Archive -->
    <v-dialog v-model="removeDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">Archive vehicle?</v-card-title>
        <v-card-text>
          {{ archiveDialogMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isRemoving" @click="removeDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="isRemoving" @click="handleRemove">
            Archive vehicle
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Hide from website — reason required -->
    <v-dialog v-model="hideReasonDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6">Hide from website?</v-card-title>
        <v-card-text>
          <p class="mb-4 text-body-2">
            VIN <strong>{{ vin }}</strong> will be removed from the public website immediately.
            It will remain in your inventory and can be shown again at any time.
          </p>
          <v-select
            v-model="hideReason"
            :items="hideReasonOptions"
            item-title="label"
            item-value="value"
            label="Reason for hiding *"
            hint="Select why this vehicle is being hidden"
            persistent-hint
            variant="outlined"
            density="comfortable"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isTogglingWebsiteVisibility" @click="cancelHideVehicle">Cancel</v-btn>
          <v-btn
            color="warning"
            variant="flat"
            :loading="isTogglingWebsiteVisibility"
            :disabled="!hideReason"
            @click="confirmHideVehicle"
          >
            Hide Vehicle
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>

  </v-container>
</template>

<script lang="ts" setup>
import { formatWebsiteVisibilityExplanation } from '@/utils/websiteVisibilityExplanation'

import { onMounted, ref, computed, watch } from 'vue'
import DOMPurify from 'dompurify'
import { useRoute, useRouter } from 'vue-router'
import {
  INVENTORY_USAGE_TYPE,
  INVENTORY_LIFECYCLE_EVENT_TYPE,
  INVENTORY_STATE,
  WEBSITE_VISIBILITY,
  WEBSITE_VISIBILITY_MODE,
} from '@jazel-auto/ise-contracts/dist/inventory.js'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import {
  cancelVehicleDeleteDraft,
  fetchVehicleByVin,
  fetchVehicleDraft,
  revertVehicleDraft,
  stageVehicleArchiveOperationalPatch,
  stageVehicleDeleteDraft,
  updateAllowFeedReclaim,
  updateVehicleTags,
} from '@/services/inventory'
import type {
  ActiveManualOverride,
  VehicleDraftIntent,
  VehicleDraftRecord,
} from '@/services/inventory'
import { usePublishStore } from '@/stores/usePublishStore'
import { useListingConfigStore } from '@/stores/listingConfig'
import { useProductSettingsStore } from '@/stores/productSettings'
import { fetchIseMappingFields, extractIseMappingFieldKeys } from '@/services/fieldMappingService'
import {
  hasVinVisibilityBlockRule,
  removeVinVisibilityBlockRule,
} from '@/utils/listingRules'
import {
  fetchVehicleOpenSearchStatus,
  fetchVehicleStatusHistory,
  returnWebsiteVisibilityToAuto,
  toggleWebsiteVisibility,
  type InventoryState,
  type InventoryUsageType,
  type WebsiteVisibility,
  type WebsiteVisibilityMode,
  type VehicleStatusHistoryItem,
} from '@/services/inventory'
import {
  getDisplayFields,
  DISPLAY_FIELD_ORDER,
  EXCLUDED_FIELDS,
  PRIORITY_FIELDS,
} from '@/constants/vehicleFieldConfig'
import {
  formatVehicleFieldDisplayValue,
  isChromeTechSpecArray,
  normalizeVehicleFieldForDisplay,
} from '@/utils/vehicleFieldDisplay'
import {
  formatVehicleHistoryReasonLabel,
  formatVehicleHistoryTimeline,
  summarizeHistoryFieldNames,
} from '@/utils/vehicleStatusHistoryFormatting'
import PublishLogsDialog from '@/components/PublishLogsDialog.vue'
import DateTimeWithDealerTooltip from '@/components/DateTimeWithDealerTooltip.vue'
import VehicleTagChips from '@/components/VehicleTagChips.vue'
import PriceInspectorLink from '@/components/inventory/PriceInspectorLink.vue'
import { usePricingHubLaunch } from '@/composables/usePricingHubLaunch'
import { usePublishedReadyCourtesySetting } from '@/composables/usePublishedReadyCourtesySetting'
import { buildManualInventoryStateOptions } from '@/utils/manualInventoryStateOptions'
import {
  dateInStockToInstantMs,
  formatDealerDateOnly,
} from '@/utils/vehicleFieldValueNormalization'

const route = useRoute()
const router = useRouter()
const accountSettings = useAccountSettingsStore()
const { separateReadyCourtesyVehicles } = usePublishedReadyCourtesySetting()
const publishStore = usePublishStore()
const listingStore = useListingConfigStore()
const productSettings = useProductSettingsStore()
const endpoint = String(import.meta.env.VITE_ISE_BACKEND_URL || '').replace(/\/+$/g, '')

const dealerTimeZone = computed(() => String(accountSettings.dealerTimeZone || '').trim())

const vin = String(route.params.vin || '')
const pricingHubLaunch = usePricingHubLaunch()
const priceInspectorUrl = computed(() => pricingHubLaunch.inspectorUrlForVin(vin))
const vehicle = ref<Record<string, unknown> | null>(null)
const activeOverrides = ref<Record<string, ActiveManualOverride>>({})
const photoCarouselIndex = ref(0)
const photoPreviewOpen = ref(false)
const photoPreviewIndex = ref(0)
const openSearchStatus = ref<{ loaded: boolean; found: boolean | null; checkedAt?: string; error?: string }>({
  loaded: false,
  found: null,
})
const historyDialogOpen = ref(false)
const historyLoaded = ref(false)
const historyLoading = ref(false)
const historyError = ref<string>('')
const historyTruncated = ref(false)
const historyItems = ref<VehicleStatusHistoryItem[]>([])

const publishLogsDialogOpen = ref(false)
const publishLogsCorrelationId = ref<string | null>(null)

// History is always shown newest first; there is no date sort control (ISE-15 IC24).
const historyItemsSorted = computed(() => {
  const out = [...historyItems.value]
  out.sort((a, b) => {
    const at = String((a as any)?.createdAt || '')
    const bt = String((b as any)?.createdAt || '')
    return bt.localeCompare(at)
  })
  return out
})

const createdDate = computed(
  () => historyItems.value.find((h) => h.eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.CREATED)?.createdAt ?? null,
)
const archivedDate = computed(() => {
  const fromHistory = historyItems.value.find(
    (h) => h.eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.ARCHIVED,
  )?.createdAt
  if (fromHistory) return fromHistory
  return archivedAtFromApi.value
})

const hasAnyVehicleTags = computed(
  () => vehicleTags.value.length > 0 || vehicleSystemTags.value.length > 0,
)

const historyDisplayItems = computed(() =>
  formatVehicleHistoryTimeline(historyItemsSorted.value, { sort: 'desc' }),
)

const copyToClipboard = async (textRaw: string) => {
  const text = String(textRaw || '').trim()
  if (!text) return

  try {
    await navigator.clipboard.writeText(text)
    snackbar.value = { show: true, message: 'Copied.', color: 'success' }
  } catch {
    // Fallback (best-effort)
    try {
      const el = document.createElement('textarea')
      el.value = text
      el.style.position = 'fixed'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      el.focus()
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      snackbar.value = { show: true, message: 'Copied.', color: 'success' }
    } catch (e) {
      snackbar.value = { show: true, message: 'Copy failed.', color: 'error' }
    }
  }
}

const openPublishLogsForCorrelationId = (cidRaw: string | undefined) => {
  const cid = String(cidRaw || '').trim()
  if (!cid) return
  publishLogsCorrelationId.value = cid
  publishLogsDialogOpen.value = true
}
const feedSources = ref<string[]>([])
const feedType = ref<'manual' | 'auto' | null>(null)
const websiteVisibilityReason = ref<string | null>(null)
const publishState = ref<'draft' | 'published'>('published') // Default published for backward compat
const websiteVisibility = ref<WebsiteVisibility>(WEBSITE_VISIBILITY.VISIBLE)
const websiteVisibilityMode = ref<WebsiteVisibilityMode>(WEBSITE_VISIBILITY_MODE.AUTO)
const isTogglingWebsiteVisibility = ref(false)

// ── Allow feed reclaim (manual vehicles only) ─────────────────────────────────
const allowFeedReclaim = ref(false)
const isTogglingAllowFeedReclaim = ref(false)

// ── Tags ─────────────────────────────────────────────────────────────────────
const vehicleTags = ref<string[]>([])
const vehicleSystemTags = ref<string[]>([])
const isEditingTags = ref(false)
const editingTags = ref<string[]>([])
const isSavingTags = ref(false)

const USER_TAG_OPTIONS_FOR_SELECT = [
  { label: 'Hot', value: 'HOT' },
  { label: 'Featured', value: 'FEATURED' },
  { label: 'Price Reduced', value: 'PRICE_REDUCED' },
  { label: 'New Arrival', value: 'NEW_ARRIVAL' },
  { label: 'Test Drive', value: 'TEST_DRIVE' },
  { label: 'Loaner', value: 'LOANER' },
  { label: 'Demo', value: 'DEMO' },
  { label: 'Wholesale', value: 'WHOLESALE' },
  { label: 'As-Is', value: 'AS_IS' },
  { label: 'Manager Special', value: 'MANAGER_SPECIAL' },
  { label: 'Certified', value: 'CERTIFIED' },
]

const startEditingTags = () => {
  editingTags.value = [...vehicleTags.value]
  isEditingTags.value = true
}

const cancelTagEdits = () => {
  isEditingTags.value = false
  editingTags.value = []
}

const saveTagEdits = async () => {
  if (!accountSettings.accountId || !vin) return
  isSavingTags.value = true
  try {
    const result = await updateVehicleTags(
      endpoint,
      accountSettings.accountId,
      vin,
      editingTags.value,
      'set',
    )
    vehicleTags.value = result.tags
    isEditingTags.value = false
    snackbar.value = { show: true, message: 'Tags saved.', color: 'success' }
  } catch (err) {
    snackbar.value = {
      show: true,
      message: `Failed to save tags: ${err instanceof Error ? err.message : String(err)}`,
      color: 'error',
    }
  } finally {
    isSavingTags.value = false
  }
}

const handleToggleAllowFeedReclaim = async (next: boolean | null) => {
  if (!accountSettings.accountId || !vin) return
  const desired = next === true
  const previous = allowFeedReclaim.value
  if (desired === previous) return
  isTogglingAllowFeedReclaim.value = true
  // Optimistic; reverted on failure.
  allowFeedReclaim.value = desired
  try {
    const result = await updateAllowFeedReclaim(
      endpoint,
      accountSettings.accountId,
      vin,
      desired,
      accountSettings.sessionToken,
    )
    allowFeedReclaim.value = result.allowFeedReclaim === true
    snackbar.value = {
      show: true,
      message: allowFeedReclaim.value
        ? 'Feed reclaim enabled for this vehicle.'
        : 'Feed reclaim disabled for this vehicle.',
      color: 'success',
    }
  } catch (err) {
    allowFeedReclaim.value = previous
    snackbar.value = {
      show: true,
      message: `Failed to update feed reclaim: ${err instanceof Error ? err.message : String(err)}`,
      color: 'error',
    }
  } finally {
    isTogglingAllowFeedReclaim.value = false
  }
}
const isPublishing = ref(false)
const availableFields = ref<string[]>([])
const displayFields = ref<string[]>([])
const restoreDialog = ref(false)
const isRestoring = ref(false)
const isRevertingRestore = ref(false)
const purgeRemovedDialog = ref(false)
const isPurgingRemoved = ref(false)
const purgeManualDialog = ref(false)
const isPurgingManual = ref(false)
const removeDialog = ref(false)
const isRemoving = ref(false)
const isCancelingDelete = ref(false)
const deleteDraftDialogOpen = ref(false)
const isDeletingDraft = ref(false)
const draftIntent = ref<VehicleDraftIntent | null>(null)
const vehicleDraft = ref<VehicleDraftRecord | null>(null)
const detailsLoadError = ref('')
const membershipStatus = ref<'CURRENT' | 'REMOVED' | 'ARCHIVED' | null>(null)
const archiveReason = ref<string | null>(null)
const archivedAtFromApi = ref<string | null>(null)
const backendStatus = ref<string | null>(null)

type RestoreWebsiteVisibilityChoice = 'AUTO' | 'VISIBLE' | 'HIDDEN'

const restoreInventoryState = ref<InventoryState>(INVENTORY_STATE.UNKNOWN)
const restoreInventoryUsageType = ref<InventoryUsageType>(INVENTORY_USAGE_TYPE.RETAIL)
const restoreWebsiteVisibilityChoice = ref<RestoreWebsiteVisibilityChoice>('AUTO')

const restoreInventoryStateOptions = computed(() =>
  buildManualInventoryStateOptions({
    includeReadyCourtesy: separateReadyCourtesyVehicles.value,
  }),
)

const restoreInventoryUsageTypeOptions: Array<{ title: string; value: InventoryUsageType }> = [
  { title: 'Retail', value: INVENTORY_USAGE_TYPE.RETAIL },
  { title: 'Wholesale', value: INVENTORY_USAGE_TYPE.WHOLESALE },
  { title: 'Demo', value: INVENTORY_USAGE_TYPE.DEMO },
  { title: 'Loaner', value: INVENTORY_USAGE_TYPE.LOANER },
  { title: 'Company vehicle', value: INVENTORY_USAGE_TYPE.COMPANY_VEHICLE },
  { title: 'Service', value: INVENTORY_USAGE_TYPE.SERVICE },
]

const restoreWebsiteVisibilityOptions: Array<{ title: string; value: RestoreWebsiteVisibilityChoice }> = [
  { title: 'Automatic', value: 'AUTO' },
  { title: 'Visible on website', value: 'VISIBLE' },
  { title: 'Hidden from website', value: 'HIDDEN' },
]

// Website visibility hide reason dialog
const hideReasonDialog = ref(false)
const hideReason = ref<string | null>(null)

const hideReasonOptions = [
  { value: 'RECON',        label: 'In recon' },
  { value: 'PRICING',      label: 'Pricing issue' },
  { value: 'PHOTOS',       label: 'Missing photos' },
  { value: 'TEMP_HOLD',    label: 'Temporary hold' },
  { value: 'RESERVED',     label: 'Reserved / deposit taken' },
  { value: 'SOLD_PENDING', label: 'Sold pending delivery / funding' },
  { value: 'OTHER',        label: 'Other' },
]
const snackbar = ref<{ show: boolean; message: string; color: 'success' | 'error' | 'info' }>({
  show: false,
  message: '',
  color: 'info',
})

// If a VIN is part of unpublished inventory changes (e.g., just manually added),
// we should not allow website visibility blocking yet — the vehicle isn't "live" until publish completes.
const isVinInUnpublishedInventory = computed(() => {
  const u = productSettings.unpublished as unknown as {
    sources?: { inventory?: { details?: { vehicles?: Array<{ vin?: unknown }> } } }
  }
  const vehicles = u?.sources?.inventory?.details?.vehicles || []
  const needle = vin.toUpperCase()
  return vehicles.some((v) => String(v?.vin || '').toUpperCase() === needle)
})

/**
 * Strict website visibility gate:
 * - Only allow hiding if backend confirms the VIN is currently listed (live).
 * - If we cannot reliably determine listing status (not found / API doesn't provide),
 *   disable the action and instruct the user to publish/wait for processing.
 */
const listedStatus = ref<{ loaded: boolean; isListed: boolean | null; source?: string }>({
  loaded: false,
  isListed: null,
})

const fromRoute = computed(() => String(route.query?.from || ''))

const backLabel = computed(() => {
  if (fromRoute.value === 'archived') return 'Back to Archived Inventory'
  if (fromRoute.value === 'unpublished') return 'Back to Unpublished Changes'
  return 'Back to Inventory'
})

const goBack = () => {
  if (fromRoute.value === 'archived') {
    router.push({ name: 'ArchivedInventory' })
    return
  }

  if (fromRoute.value === 'unpublished') {
    router.push({ name: 'UnpublishedChanges' })
    return
  }

  router.push('/inventory')
}

const goToEdit = () => {
  router.push({
    path: `/inventory/${vin}/edit`,
    query: route.query?.from ? { from: route.query.from } : undefined,
  })
}

// Source-of-truth is feedType (CurrentInventory.type); feedSources is best-effort enrichment only.
const isManualVehicle = computed(() => {
  return feedType.value === 'manual'
})

// Feed-backed (auto) vehicles are editable too: their display-field edits stage manual overrides
// on the VehicleDraft (committed at publish), never custom_feed conversion. Unknown feedType is
// treated as not-editable to avoid misrouting.
const isFeedBackedVehicle = computed(() => {
  return feedType.value === 'auto'
})

// Edit is offered for current (non-removed, non-staged-delete) manual OR feed-backed vehicles.
const canEditVehicle = computed(() => {
  return (
    (isManualVehicle.value || isFeedBackedVehicle.value) &&
    !isRemovedFromInventory.value &&
    !isStagedForDelete.value
  )
})

const isArchivedVehicle = computed(() => {
  if (isHardDeleted.value) return false
  if (membershipStatus.value === 'CURRENT') return false
  if (fromRoute.value === 'archived') return true
  if (membershipStatus.value === 'ARCHIVED') return true
  if (archiveReason.value) return true
  if (archivedAtFromApi.value) return true
  if (inventoryState.value === INVENTORY_STATE.ARCHIVED) return true
  const eventType = String(backendStatus.value || '').trim().toUpperCase()
  if (eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.ARCHIVED) return true
  if (
    membershipStatus.value === 'REMOVED' &&
    (archiveReason.value || archivedAtFromApi.value || archivedDate.value)
  ) {
    return true
  }
  return false
})

const isRemovedFromInventory = computed(() => {
  if (membershipStatus.value === 'REMOVED' || membershipStatus.value === 'ARCHIVED') return true
  const s = String(backendStatus.value || '').trim().toUpperCase()
  return s === 'REMOVED' || s === 'HARD_DELETED'
})

const isHardDeleted = computed(() => String(backendStatus.value || '').trim().toUpperCase() === 'HARD_DELETED')

const canRestoreArchivedVehicle = computed(() => {
  // Restore is available for archived records that still have ArchivedInventory/S3 history.
  return isArchivedVehicle.value && isRemovedFromInventory.value && !isHardDeleted.value && !hasPendingRestoreDraft.value
})

const canPurgeRemovedFeedVin = computed(() => {
  // Only allow purge for removed FEED (auto) vehicles. Manual already has hard-delete flow.
  return isRemovedFromInventory.value && !isManualVehicle.value && !hasPendingRestoreDraft.value
})

const canPurgeRemovedManualVin = computed(() => {
  // Manual "Delete record" is a separate hard-delete path; only allow when removed and not already hard-deleted.
  return isRemovedFromInventory.value && isManualVehicle.value && !isHardDeleted.value && !hasPendingRestoreDraft.value
})

// Feed-backed archive is staged as an operational PATCH draft carrying
// operationalPatch.inventoryState=ARCHIVED (not intent=DELETE like manual archive).
const isStagedForArchiveOperational = computed(() => {
  return (
    isFeedBackedVehicle.value &&
    draftIntent.value === 'PATCH' &&
    String(vehicleDraft.value?.operationalPatch?.inventoryState || '').trim().toUpperCase() ===
      'ARCHIVED'
  )
})

// Unified "archive is staged" gate covering both forms: manual DELETE draft and
// feed-backed operational ARCHIVED draft.
const isStagedForArchive = computed(() => {
  return isStagedForDelete.value || isStagedForArchiveOperational.value
})

// Feed-backed-only guard: a non-archive PATCH draft already exists. The feed-backed archive
// publish path is operational-only, so we must not merge field edits into an ARCHIVED draft.
const hasFeedBackedNonArchivePatchDraft = computed(() => {
  return (
    isFeedBackedVehicle.value &&
    draftIntent.value === 'PATCH' &&
    !isStagedForArchiveOperational.value
  )
})

const shouldShowArchiveVehicleAction = computed(() => {
  return (
    !isUnpublishedCreateDraft.value &&
    (isManualVehicle.value || isFeedBackedVehicle.value) &&
    !isRemovedFromInventory.value &&
    !isStagedForArchive.value
  )
})

const canArchiveVehicle = computed(() => {
  return (
    shouldShowArchiveVehicleAction.value &&
    !isVinInUnpublishedInventory.value &&
    !hasFeedBackedNonArchivePatchDraft.value
  )
})

const isUnpublishedCreateDraft = computed(() => {
  return isManualVehicle.value && publishState.value === 'draft' && draftIntent.value === 'CREATE'
})

const hasPendingPatchDraft = computed(() => {
  return draftIntent.value === 'PATCH'
})

const hasPendingRestoreDraft = computed(() => {
  return draftIntent.value === 'RESTORE'
})

const hasAnyPendingDraft = computed(() => {
  return Boolean(draftIntent.value)
})

const vehicleDetailsNotice = computed<null | { type: 'info' | 'warning'; text: string }>(() => {
  if (!hasAnyPendingDraft.value) return null

  if (isUnpublishedCreateDraft.value) {
    return {
      type: 'info',
      text: 'This manual vehicle is saved as a draft. Publish changes before it can appear on the website or use website visibility controls.',
    }
  }

  // Archive-staged must be checked before the generic PATCH notice: a feed-backed archive is
  // an operational PATCH draft (intent=PATCH), so it would otherwise fall into the PATCH branch.
  if (isStagedForArchive.value) {
    return {
      type: 'warning',
      text: 'Archive is staged. Publish changes to move this vehicle out of current inventory.',
    }
  }

  if (hasPendingPatchDraft.value) {
    return {
      type: 'info',
      text: 'This vehicle has unpublished changes. Publish changes to apply the edits to the website.',
    }
  }

  if (hasPendingRestoreDraft.value) {
    return {
      type: 'info',
      text: 'Restore is staged. Publish changes to return this vehicle to current inventory.',
    }
  }

  return null
})

const shouldShowUnpublishedChangesAction = computed(() => {
  return hasPendingPatchDraft.value && !isStagedForArchiveOperational.value
})

const shouldShowRevertArchiveInNotice = computed(() => {
  return isStagedForArchive.value
})

const shouldShowRevertRestoreInNotice = computed(() => {
  return hasPendingRestoreDraft.value
})

const inventoryStateLabels: Record<string, string> = {
  ON_ORDER: 'On order',
  IN_PRODUCTION: 'In production',
  IN_TRANSIT: 'In transit',
  READY_FOR_SALE: 'Ready for sale',
  READY_COURTESY: 'Ready Courtesy',
  IN_SERVICE: 'In service',
  UNKNOWN: 'Unknown',
  RESERVED: 'Reserved',
  SOLD: 'Sold',
  ARCHIVED: 'Archived',
}

const inventoryUsageTypeLabels: Record<string, string> = {
  RETAIL: 'Retail',
  WHOLESALE: 'Wholesale',
  DEMO: 'Demo',
  LOANER: 'Loaner',
  COMPANY_VEHICLE: 'Company vehicle',
  SERVICE: 'Service',
}

const formatInventoryStateLabel = (value?: unknown) => {
  const key = String(value || '').trim().toUpperCase()
  return inventoryStateLabels[key] || ''
}

const formatInventoryUsageTypeLabel = (value?: unknown) => {
  const key = String(value || '').trim().toUpperCase()
  return inventoryUsageTypeLabels[key] || ''
}

const formatRestoreWebsiteVisibilityLabel = (patch?: Record<string, unknown> | null) => {
  const mode = String(patch?.websiteVisibilityMode || '').trim().toUpperCase()
  const visibility = String(patch?.websiteVisibility || '').trim().toUpperCase()

  if (mode === 'AUTO') return 'Automatic website visibility'
  if (visibility === 'VISIBLE') return 'Visible on website'
  if (visibility === 'HIDDEN') return 'Hidden from website'
  return ''
}

const pendingRestoreOperationalPatch = computed(() => {
  if (!hasPendingRestoreDraft.value) return null
  return vehicleDraft.value?.operationalPatch ?? null
})

const restoreStagedSummary = computed(() => {
  const patch = pendingRestoreOperationalPatch.value
  if (!patch) return ''

  const parts = [
    formatInventoryStateLabel(patch.inventoryState),
    formatInventoryUsageTypeLabel(patch.inventoryUsageType),
    formatRestoreWebsiteVisibilityLabel(patch as Record<string, unknown>),
  ].filter(Boolean)

  return parts.length ? `Restores as: ${parts.join(' · ')}` : ''
})

const shouldShowDeleteDraftAction = computed(() => {
  return isUnpublishedCreateDraft.value
})

const goToUnpublishedChanges = () => {
  router.push('/unpublished')
}

const isPublishedManualVehicle = computed(() => {
  return isManualVehicle.value && publishState.value !== 'draft' && !isRemovedFromInventory.value
})

const inventoryState = computed(() => {
  const raw = (vehicle.value as Record<string, unknown> | null)?.inventoryState
  return typeof raw === 'string' ? raw : null
})

const inventoryStatusLabel = computed(() => {
  if (isHardDeleted.value) return 'Deleted record'
  if (isArchivedVehicle.value) return 'Archived'
  if (isRemovedFromInventory.value) return 'Removed'
  return 'Current'
})

const websiteStatusLabel = computed(() => {
  if (isArchivedVehicle.value || isRemovedFromInventory.value) return 'Not applicable'

  return formatWebsiteVisibilityExplanation({
    websiteVisibility: websiteVisibility.value, websiteVisibilityMode: websiteVisibilityMode.value, websiteVisibilityReason: websiteVisibilityReason.value,
    inventoryState: (vehicle.value as any)?.inventoryState, websiteVisibilityExplanation: (vehicle.value as any)?.websiteVisibilityExplanation,
  })
})

const websiteVisibilityModeLabel = computed(() => {
  if (isArchivedVehicle.value || isRemovedFromInventory.value) return ''
  return websiteVisibilityMode.value === WEBSITE_VISIBILITY_MODE.MANUAL
    ? 'Manual override active'
    : 'Automatic'
})

const canShowWebsiteVisibilityControls = computed(() => {
  return !isUnpublishedCreateDraft.value && !isRemovedFromInventory.value && !isArchivedVehicle.value
})

const isAutomaticSystemHidden = computed(() => {
  if (websiteVisibilityMode.value !== WEBSITE_VISIBILITY_MODE.AUTO) return false
  if (websiteVisibility.value !== WEBSITE_VISIBILITY.HIDDEN) return false
  const reason = String(websiteVisibilityReason.value || '').trim().toLowerCase()
  return reason === 'hidden_by_rules' || reason === 'srp_missing'
})

const showShowOnWebsiteAction = computed(() => {
  return canShowWebsiteVisibilityControls.value
    && websiteVisibility.value === WEBSITE_VISIBILITY.HIDDEN
    && !isAutomaticSystemHidden.value
})

const showHideFromWebsiteAction = computed(() => {
  return canShowWebsiteVisibilityControls.value
    && websiteVisibility.value === WEBSITE_VISIBILITY.VISIBLE
})

const showUseAutomaticVisibilityAction = computed(() => {
  return canShowWebsiteVisibilityControls.value
    && websiteVisibilityMode.value === WEBSITE_VISIBILITY_MODE.MANUAL
})

const showArchivedDateInStatusSummary = computed(() => isArchivedVehicle.value)

const archiveReasonLabel = computed(() => {
  const reason =
    archiveReason.value ||
    (typeof (vehicle.value as Record<string, unknown> | null)?.archiveReason === 'string'
      ? String((vehicle.value as Record<string, unknown>).archiveReason)
      : '')
  const trimmed = reason.trim()
  if (!trimmed) return '—'
  return formatVehicleHistoryReasonLabel(trimmed)
})

const sourceLabel = computed(() => {
  return isManualVehicle.value ? 'Manual' : 'Feed'
})

const hasManualVisibilityBlockRule = computed(() => {
  return hasVinVisibilityBlockRule(
    listingStore.listingConfig,
    vin,
  )
})

// Live website hidden state comes from the canonical backend websiteVisibility enum.
const liveIsHiddenFromSite = computed(() => {
  if (websiteVisibility.value === WEBSITE_VISIBILITY.VISIBLE) return false
  if (websiteVisibility.value === WEBSITE_VISIBILITY.HIDDEN) return true
  return listedStatus.value.isListed === false
})

const applyListedStatusFromBackend = (statusRaw: unknown, source: string) => {
  const status = typeof statusRaw === 'string' ? statusRaw.toUpperCase() : ''
  if (status === INVENTORY_LIFECYCLE_EVENT_TYPE.ARCHIVED) {
    listedStatus.value = { loaded: true, isListed: false, source }
    return
  }
  if (
    status === INVENTORY_LIFECYCLE_EVENT_TYPE.CREATED ||
    status === INVENTORY_LIFECYCLE_EVENT_TYPE.RESTORED ||
    status === INVENTORY_LIFECYCLE_EVENT_TYPE.UPDATED ||
    status === INVENTORY_LIFECYCLE_EVENT_TYPE.WEBSITE_VISIBILITY_CHANGED
  ) {
    listedStatus.value = { loaded: true, isListed: true, source }
    return
  }

  // If backend could not provide an eventType, do not guess.
  listedStatus.value = { loaded: true, isListed: null, source: `${source}_no_event_type` }
}

const fetchListedStatus = async (statusOverride?: unknown) => {
  if (!accountSettings.accountId || !vin) {
    listedStatus.value = { loaded: true, isListed: null, source: 'missing_params' }
    return
  }

  // Prefer the already-fetched vehicle status to avoid a duplicate network call.
  if (statusOverride) {
    applyListedStatusFromBackend(statusOverride, 'vehicle_details_status')
    return
  }

  try {
    // Fallback: use VIN-specific lookup to avoid pagination/ambiguity.
    const res = await fetchVehicleByVin(endpoint, accountSettings.accountId, vin, true)
    applyListedStatusFromBackend(res?.eventType, 'vehicle_by_vin_status')
  } catch (e) {
    console.error('Failed to determine listed status', e)
    listedStatus.value = { loaded: true, isListed: null, source: 'error' }
  }
}

const fetchOpenSearchStatus = async () => {
  const jazelId = accountSettings.accountId
  if (!jazelId || !vin) {
    openSearchStatus.value = { loaded: true, found: null, error: 'missing_params' }
    return
  }

  // Mark as "loading" (loaded=false)
  openSearchStatus.value = { loaded: false, found: null }
  try {
    const res = await fetchVehicleOpenSearchStatus(endpoint, jazelId, vin)
    openSearchStatus.value = {
      loaded: true,
      found: typeof res?.found === 'boolean' ? res.found : null,
      checkedAt: typeof res?.checkedAt === 'string' ? res.checkedAt : undefined,
    }
  } catch (e) {
    openSearchStatus.value = {
      loaded: true,
      found: null,
      error: e instanceof Error ? e.message : 'unknown_error',
    }
  }
}

const openSearchChip = computed(() => {
  if (!openSearchStatus.value.loaded) {
    return { text: 'OpenSearch: checking…', color: 'grey', title: 'Checking whether this VIN is live in OpenSearch.' }
  }

  if (openSearchStatus.value.found === true) {
    return { text: 'OpenSearch: live', color: 'success', title: openSearchStatus.value.checkedAt ? `Checked: ${openSearchStatus.value.checkedAt}` : 'Confirmed present in OpenSearch.' }
  }

  if (openSearchStatus.value.found === false) {
    return { text: 'OpenSearch: not found', color: 'warning', title: openSearchStatus.value.checkedAt ? `Checked: ${openSearchStatus.value.checkedAt}` : 'No OpenSearch document found for this VIN.' }
  }

  return {
    text: 'OpenSearch: unknown',
    color: 'grey',
    title: openSearchStatus.value.error ? `Couldn’t confirm (${openSearchStatus.value.error}).` : 'Couldn’t confirm OpenSearch status.',
  }
})

// Visibility rows already say "Visibility" in their operation chip, so their change line does
// not repeat the "Website Visibility:" label.
const historyChangeLine = (row: VehicleStatusHistoryItem, line: string): string => {
  const label = 'Website Visibility: '
  return String(row.eventType || '').toUpperCase() === INVENTORY_LIFECYCLE_EVENT_TYPE.WEBSITE_VISIBILITY_CHANGED &&
    line.startsWith(label)
    ? line.slice(label.length)
    : line
}

// Splits a change line so VISIBLE / HIDDEN can use the same colors as the visibility badges.
const historyChangeParts = (
  row: VehicleStatusHistoryItem,
  line: string,
): Array<{ text: string; tone: 'visible' | 'hidden' | null }> =>
  historyChangeLine(row, line)
    .split(/\b(VISIBLE|HIDDEN)\b/)
    .filter((text) => text !== '')
    .map((text) => ({ text, tone: text === 'VISIBLE' ? 'visible' : text === 'HIDDEN' ? 'hidden' : null }))

const historyEventColor = (eventTypeRaw: string) => {
  const eventType = String(eventTypeRaw || '').toUpperCase()
  if (eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.ARCHIVED) return 'error'
  if (
    eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.CREATED ||
    eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.RESTORED
  ) return 'success'
  if (
    eventType.endsWith('_CHANGED') ||
    eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.UPDATED ||
    eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.FEED_RECLAIMED_MANUAL_RESTORE
  )
    return 'info'
  return 'grey'
}

const formatISODate = (value: string | null | undefined): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const openHistoryDialog = async () => {
  historyDialogOpen.value = true
  if (historyLoaded.value || historyLoading.value) return

  const jazelId = accountSettings.accountId
  if (!jazelId || !vin) {
    historyError.value = 'Missing accountId/VIN'
    return
  }

  historyLoading.value = true
  historyError.value = ''
  try {
    const res = await fetchVehicleStatusHistory(endpoint, jazelId, vin)
    historyItems.value = Array.isArray(res?.items) ? (res.items as VehicleStatusHistoryItem[]) : []
    historyTruncated.value = !!res?.truncated
    historyLoaded.value = true
  } catch (e) {
    historyError.value = e instanceof Error ? e.message : 'Failed to load history'
  } finally {
    historyLoading.value = false
  }
}

const disableRemoveReason = computed(() => {
  if (isStagedForArchive.value) {
    return 'This vehicle archive is already staged. Publish changes to archive it, or revert the staged archive.'
  }
  if (hasFeedBackedNonArchivePatchDraft.value) {
    return 'This vehicle has unpublished edits. Publish or revert them before archiving.'
  }
  if (isVinInUnpublishedInventory.value) {
    return 'This vehicle has pending changes. Publish changes first, then try archiving it again.'
  }
  if (!isManualVehicle.value && !isFeedBackedVehicle.value) {
    return 'This vehicle type cannot be archived from this action.'
  }
  return ''
})

const disableRemove = computed(() => {
  return isRemoving.value || isVinInUnpublishedInventory.value || !canArchiveVehicle.value
})

const isStagedForDelete = computed(() => draftIntent.value === 'DELETE')

const archiveDialogMessage = computed(() =>
  isFeedBackedVehicle.value
    ? 'This will remove the vehicle from current inventory, hide it from the website, and prevent the feed from adding it back after publish. The vehicle will move to Archived Inventory and can be restored later.'
    : 'This will remove the vehicle from current inventory and hide it from the website. The vehicle will move to Archived Inventory and can be restored later.',
)

const formatPrice = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)

const getEffectiveDetailFieldValue = (field: string, fallbackValue: unknown): unknown => {
  const active = activeOverrides.value[field]
  if (active && Object.prototype.hasOwnProperty.call(active, 'manualValue')) {
    return active.manualValue
  }
  return fallbackValue
}

const getVehicleDetailFieldValue = (field: string): unknown => {
  const raw = getEffectiveDetailFieldValue(
    field,
    (vehicle.value as Record<string, unknown> | null)?.[field],
  )
  const normalized = normalizeVehicleFieldForDisplay(field, raw)

  // If a scalar spec field is empty but tech_specs holds the Chrome array,
  // recover the matching value for display (covers vehicles saved before the
  // prefill mapping fix).
  if (
    (normalized == null || normalized === '') &&
    (field === 'engine_displacement' || field.startsWith('mpg_'))
  ) {
    const techSpecs = (vehicle.value as Record<string, unknown> | null)?.tech_specs
    return normalizeVehicleFieldForDisplay(field, techSpecs)
  }

  return normalized
}

const hasActivePriceOverride = computed(() => Boolean(activeOverrides.value.price))

// Normalized view model for inconsistently named SRP fields
const view = computed(() => {
  const v = (vehicle.value || {}) as Record<string, unknown>
  const toNumber = (x: unknown) => {
    if (x == null) return undefined
    const n = typeof x === 'string' ? parseFloat(x) : typeof x === 'number' ? x : NaN
    return Number.isFinite(n) ? n : undefined
  }
  const toString = (x: unknown) => (x == null ? undefined : String(x))
  const mapCondition = (c: unknown) => {
    const s = String(c || '').toUpperCase()
    if (s === 'N') return 'New'
    if (s === 'U') return 'Used'
    if (s === 'C') return 'CPO'
    return s || undefined
  }
  const priceRaw = getEffectiveDetailFieldValue(
    'price',
    v['price'] ?? v['display_price'] ?? v['msrp'],
  )
  const featuresVal = v['features']
  const genericEquipmentVal = v['generic_equipment']
  const featuresArray: string[] = Array.isArray(featuresVal)
    ? (featuresVal as string[])
    : typeof genericEquipmentVal === 'string'
      ? String(genericEquipmentVal)
          .split('|')
          .map((s: string) => s.trim())
          .filter(Boolean)
      : []
  const dealerPhotosVal = v['dealer_photos']
  const photos: string[] = Array.isArray(dealerPhotosVal) ? (dealerPhotosVal as string[]) : []
  return {
    year: toNumber(v['year'] ?? v['year_sidebar']),
    make: toString(v['make'] ?? v['make_sidebar']),
    model: toString(v['model'] ?? v['model_sidebar']),
    trim: toString(v['trim'] ?? v['trim_sidebar']),
    stock: toString(v['stock_number']),
    price: toNumber(priceRaw),
    condition: mapCondition(v['condition'] ?? v['condition_sidebar']),
    bodyType: toString(v['body_type'] ?? v['body_type_sidebar'] ?? v['body']),
    drivetrain: toString(v['drivetrain'] ?? v['drivetrain_sidebar']),
    transmission: toString(v['transmission'] ?? v['transmission_sidebar']),
    engine: toString(v['engine']),
    exteriorColor: toString(
      v['exterior_color'] ?? v['exterior_factory_color'] ?? v['exterior_color_sidebar'],
    ),
    interiorColor: toString(
      v['interior_color'] ?? v['interior_factory_color'] ?? v['interior_color_sidebar'],
    ),
    mileage: toNumber(v['mileage']),
    comments: toString(v['dealer_comments'] ?? v['dealer_description']),
    features: featuresArray,
    photos,
  }
})

const sanitizedComments = computed(() => {
  const raw = view.value.comments
  if (!raw) return ''

  return DOMPurify.sanitize(raw, {
    USE_PROFILES: { html: true },
  })
})

const photoPreviewSlides = computed(() => view.value.photos || [])

const photoPages = computed(() => {
  const photos = view.value.photos || []
  const pages: string[][] = []
  for (let i = 0; i < photos.length; i += 4) {
    pages.push(photos.slice(i, i + 4))
  }
  return pages
})

watch(
  () => photoPages.value.length,
  () => {
    photoCarouselIndex.value = 0
  },
)

watch(
  () => photoPreviewSlides.value.length,
  (len) => {
    if (len <= 0) {
      photoPreviewOpen.value = false
      photoPreviewIndex.value = 0
      return
    }
    if (photoPreviewIndex.value >= len) photoPreviewIndex.value = 0
  },
)

const openPhotoPreview = (idx: number) => {
  const slides = photoPreviewSlides.value
  if (!slides.length) return
  photoPreviewIndex.value = Math.max(0, Math.min(idx, slides.length - 1))
  photoPreviewOpen.value = true
}

const fetchVehicleDraftForDetails = async (): Promise<VehicleDraftRecord | null> => {
  try {
    const draftResp = await fetchVehicleDraft(
      endpoint,
      accountSettings.accountId,
      vin,
      accountSettings.sessionToken,
    )
    const draft = draftResp?.draft ?? null
    draftIntent.value = draft?.intent ?? null
    vehicleDraft.value = draft
    return draft
  } catch (e) {
    draftIntent.value = null
    vehicleDraft.value = null
    // Non-fatal: viewing can proceed from the live snapshot when one exists.
    console.warn('⚠️ Failed to load vehicle draft for details (non-fatal):', e)
    return null
  }
}

const applyCreateDraftToDetails = (draft: VehicleDraftRecord) => {
  activeOverrides.value = {}
  const nextVehicle: Record<string, unknown> = { vin }

  if (draft.fields && typeof draft.fields === 'object') {
    for (const [key, entry] of Object.entries(draft.fields)) {
      nextVehicle[key] = entry?.value
    }
  }

  if (draft.operationalPatch && typeof draft.operationalPatch === 'object') {
    Object.assign(nextVehicle, draft.operationalPatch)
    if (!Object.prototype.hasOwnProperty.call(nextVehicle, 'state')) {
      const inventoryStateValue = draft.operationalPatch.inventoryState
      if (
        inventoryStateValue !== undefined &&
        inventoryStateValue !== null &&
        String(inventoryStateValue).trim() !== ''
      ) {
        const inventoryStateLabel = formatInventoryStateLabel(inventoryStateValue)
        nextVehicle.state = inventoryStateLabel || inventoryStateValue
      }
    }
  }

  vehicle.value = nextVehicle
  feedType.value = 'manual'
  feedSources.value = ['manual_add']
  membershipStatus.value = 'CURRENT'
  backendStatus.value = null
  archiveReason.value = null
  archivedAtFromApi.value = null
  publishState.value = 'draft'

  const rawTags = nextVehicle.tags
  vehicleTags.value = Array.isArray(rawTags) ? rawTags.filter((t: unknown) => typeof t === 'string') : []
  const rawSystemTags = nextVehicle.systemTags
  vehicleSystemTags.value = Array.isArray(rawSystemTags)
    ? rawSystemTags.filter((t: unknown) => typeof t === 'string')
    : []

  const wv = nextVehicle.websiteVisibility
  websiteVisibility.value =
    wv === WEBSITE_VISIBILITY.HIDDEN ? WEBSITE_VISIBILITY.HIDDEN : WEBSITE_VISIBILITY.VISIBLE
  const wvMode = nextVehicle.websiteVisibilityMode
  websiteVisibilityMode.value =
    wvMode === WEBSITE_VISIBILITY_MODE.MANUAL
      ? WEBSITE_VISIBILITY_MODE.MANUAL
      : WEBSITE_VISIBILITY_MODE.AUTO
}

const loadVehicleDetails = async () => {
  if (!accountSettings.accountId || !vin) return

  detailsLoadError.value = ''
  draftIntent.value = null
  vehicleDraft.value = null
  activeOverrides.value = {}

  try {
    // includeArchived=true so archived vehicles can still show last known SRP snapshot from S3.
    const resp = await fetchVehicleByVin(endpoint, accountSettings.accountId, vin, true)
    const activeMap: Record<string, ActiveManualOverride> = {}
    for (const ov of resp.manualOverrides?.active ?? []) {
      if (ov?.field) activeMap[ov.field] = ov
    }
    activeOverrides.value = activeMap
    vehicle.value = resp.vehicle ?? null
    feedType.value = resp.feedType ?? null
    feedSources.value = resp.feedSources || []
    membershipStatus.value = resp.membershipStatus ?? null
    backendStatus.value = resp.eventType ?? null
    archiveReason.value =
      (typeof resp.archiveReason === 'string' && resp.archiveReason.trim()
        ? resp.archiveReason.trim()
        : null) ??
      (typeof (resp.vehicle as Record<string, unknown> | null)?.archiveReason === 'string'
        ? String((resp.vehicle as Record<string, unknown>).archiveReason).trim() || null
        : null)
    archivedAtFromApi.value =
      (typeof resp.archivedAt === 'string' && resp.archivedAt.trim()
        ? resp.archivedAt.trim()
        : null) ??
      (typeof (resp.vehicle as Record<string, unknown> | null)?.archivedAt === 'string'
        ? String((resp.vehicle as Record<string, unknown>).archivedAt).trim() || null
        : null)
    websiteVisibilityReason.value =
      (resp.vehicle?.websiteVisibilityReason as string | undefined) ??
      resp.websiteVisibilityReason ??
      null
    publishState.value = resp.publishState ?? 'published'
    allowFeedReclaim.value = resp.allowFeedReclaim === true
    const wv = resp.websiteVisibility ?? (resp.vehicle as any)?.websiteVisibility
    websiteVisibility.value =
      wv === WEBSITE_VISIBILITY.HIDDEN ? WEBSITE_VISIBILITY.HIDDEN : WEBSITE_VISIBILITY.VISIBLE
    const wvMode = resp.vehicle?.websiteVisibilityMode ?? (resp as any).websiteVisibilityMode
    websiteVisibilityMode.value =
      wvMode === WEBSITE_VISIBILITY_MODE.MANUAL
        ? WEBSITE_VISIBILITY_MODE.MANUAL
        : WEBSITE_VISIBILITY_MODE.AUTO

    // tags: read from the SRP vehicle data (merged by srpS3Creator from CurrentInventory)
    const rawTags = (resp.vehicle as any)?.tags
    vehicleTags.value = Array.isArray(rawTags) ? rawTags.filter((t: unknown) => typeof t === 'string') : []
    const rawSystemTags = (resp.vehicle as any)?.systemTags
    vehicleSystemTags.value = Array.isArray(rawSystemTags) ? rawSystemTags.filter((t: unknown) => typeof t === 'string') : []

    const draft = await fetchVehicleDraftForDetails()

    if (draft?.intent === 'CREATE') {
      applyCreateDraftToDetails(draft)
    } else if (!vehicle.value) {
      detailsLoadError.value = draft?.intent
        ? `Unable to load the live vehicle for this ${draft.intent} draft.`
        : 'Unable to load vehicle details.'
    }

    const tasks: Array<Promise<unknown>> = []

    // Kick off secondary fetches in parallel (best-effort; none should block the page).
    tasks.push(fetchFields())
    tasks.push(listingStore.getListingConfig())
    tasks.push(fetchListedStatus(resp.eventType))
    tasks.push(fetchOpenSearchStatus())
    tasks.push(
      fetchVehicleStatusHistory(endpoint, accountSettings.accountId, vin)
        .then((res) => {
          historyItems.value = Array.isArray(res?.items) ? (res.items as VehicleStatusHistoryItem[]) : []
          historyTruncated.value = !!res?.truncated
          historyLoaded.value = true
        })
        .catch((e) => {
          console.warn('⚠️ Failed to load vehicle lifecycle history (non-fatal):', e)
        }),
    )
    tasks.push(
      publishStore.refreshUnpublishedChangesSummary().catch((e) => {
        console.warn('⚠️ Failed to refresh draft vehicle count (non-fatal):', e)
      }),
    )

    await Promise.allSettled(tasks)
  } catch (error) {
    const draft = await fetchVehicleDraftForDetails()
    if (draft?.intent === 'CREATE') {
      applyCreateDraftToDetails(draft)
      await Promise.allSettled([
        fetchFields(),
        listingStore.getListingConfig(),
        publishStore.refreshUnpublishedChangesSummary().catch((e) => {
          console.warn('⚠️ Failed to refresh draft vehicle count (non-fatal):', e)
        }),
      ])
      return
    }

    detailsLoadError.value = draft?.intent
      ? `Unable to load the live vehicle for this ${draft.intent} draft.`
      : 'Unable to load vehicle details.'
    console.error('Failed to load vehicle details', error)
  }
}

onMounted(async () => {
  await loadVehicleDetails()
})

const normalizeRestoreInventoryState = (raw: unknown): InventoryState => {
  const value = String(raw || '').trim()
  return restoreInventoryStateOptions.value.some((option) => option.value === value)
    ? (value as InventoryState)
    : INVENTORY_STATE.UNKNOWN
}

const normalizeRestoreInventoryUsageType = (raw: unknown): InventoryUsageType => {
  const value = String(raw || '').trim()
  return restoreInventoryUsageTypeOptions.some((option) => option.value === value)
    ? (value as InventoryUsageType)
    : INVENTORY_USAGE_TYPE.RETAIL
}

const openRestoreDialog = () => {
  const current = (vehicle.value || {}) as Record<string, unknown>
  restoreInventoryState.value = normalizeRestoreInventoryState(
    current.previousInventoryState || current.inventoryState,
  )
  restoreInventoryUsageType.value = normalizeRestoreInventoryUsageType(current.inventoryUsageType)
  restoreWebsiteVisibilityChoice.value = 'AUTO'
  restoreDialog.value = true
}

const handleRestore = async () => {
  if (!accountSettings.accountId || !vin) return
  isRestoring.value = true
  try {
    const token = accountSettings.sessionToken
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.authorization = `Bearer ${token}`

    const resp = await fetch(`${endpoint}/custom-feed/restore`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        jazelId: accountSettings.accountId,
        vin,
        operationalPatch: {
          inventoryState: restoreInventoryState.value,
          inventoryUsageType: restoreInventoryUsageType.value,
          websiteVisibilityMode:
            restoreWebsiteVisibilityChoice.value === 'AUTO'
              ? WEBSITE_VISIBILITY_MODE.AUTO
              : WEBSITE_VISIBILITY_MODE.MANUAL,
          ...(restoreWebsiteVisibilityChoice.value === 'VISIBLE'
            ? { websiteVisibility: WEBSITE_VISIBILITY.VISIBLE }
            : restoreWebsiteVisibilityChoice.value === 'HIDDEN'
              ? { websiteVisibility: WEBSITE_VISIBILITY.HIDDEN }
              : {}),
        },
      }),
    })
    const json = (await resp.json().catch(() => ({}))) as any
    if (!resp.ok) {
      throw new Error(json?.message || 'Failed to restore vehicle')
    }

    restoreDialog.value = false
    snackbar.value = {
      show: true,
      message: 'Vehicle restored as draft — publish to make live.',
      color: 'success',
    }

    await loadVehicleDetails()
    try {
      await publishStore.refreshUnpublishedChangesSummary()
    } catch (e) {
      console.warn('⚠️ Failed to refresh draft vehicle count after restore (non-fatal):', e)
    }
  } catch (e) {
    console.error('Failed to restore vehicle', e)
    const msg = e instanceof Error ? e.message : 'Failed to restore vehicle'
    snackbar.value = { show: true, message: msg, color: 'error' }
  } finally {
    isRestoring.value = false
  }
}

const handleRevertRestore = async () => {
  if (!accountSettings.accountId || !vin) return
  isRevertingRestore.value = true
  try {
    await revertVehicleDraft(endpoint, accountSettings.accountId, vin, accountSettings.sessionToken)
    draftIntent.value = null
    vehicleDraft.value = null
    snackbar.value = {
      show: true,
      message: 'Restore reverted.',
      color: 'success',
    }
    await loadVehicleDetails()
    try {
      await publishStore.refreshUnpublishedChangesSummary()
    } catch (e) {
      console.warn('⚠️ Failed to refresh draft vehicle count after reverting restore (non-fatal):', e)
    }
  } catch (e) {
    console.error('Failed to revert restore', e)
    const msg = e instanceof Error ? e.message : 'Failed to revert restore'
    snackbar.value = { show: true, message: msg, color: 'error' }
  } finally {
    isRevertingRestore.value = false
  }
}

const handlePurgeRemovedFeedVin = async () => {
  if (!accountSettings.accountId || !vin) return
  isPurgingRemoved.value = true
  try {
    const token = accountSettings.sessionToken
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.authorization = `Bearer ${token}`

    const resp = await fetch(`${endpoint}/inventory`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ action: 'purgeRemovedFeedVin', jazelId: accountSettings.accountId, vin }),
    })
    const json = (await resp.json().catch(() => ({}))) as any
    if (!resp.ok) {
      throw new Error(json?.message || 'Failed to delete record')
    }

    purgeRemovedDialog.value = false
    snackbar.value = { show: true, message: 'Record deleted.', color: 'success' }

    // Navigate back to inventory (VIN no longer resolvable after purge).
    router.push({ name: 'Inventory' })
  } catch (e) {
    console.error('Failed to purge removed feed VIN', e)
    const msg = e instanceof Error ? e.message : 'Failed to delete record'
    snackbar.value = { show: true, message: msg, color: 'error' }
  } finally {
    isPurgingRemoved.value = false
  }
}

const handlePurgeRemovedManualVin = async () => {
  if (!accountSettings.accountId || !vin) return
  isPurgingManual.value = true
  try {
    const token = accountSettings.sessionToken
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.authorization = `Bearer ${token}`

    const resp = await fetch(`${endpoint}/inventory`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ action: 'purgeRemovedManualVin', jazelId: accountSettings.accountId, vin }),
    })
    const json = (await resp.json().catch(() => ({}))) as any
    if (!resp.ok) {
      throw new Error(json?.message || 'Failed to delete record')
    }

    purgeManualDialog.value = false
    snackbar.value = { show: true, message: 'Record deleted.', color: 'success' }

    // Navigate back to inventory (VIN record is now hard-deleted/purged).
    router.push({ name: 'Inventory' })
  } catch (e) {
    console.error('Failed to purge removed manual VIN', e)
    const msg = e instanceof Error ? e.message : 'Failed to delete record'
    snackbar.value = { show: true, message: msg, color: 'error' }
  } finally {
    isPurgingManual.value = false
  }
}

// Fetch available fields to mirror Add Vehicle page
const fetchFields = async () => {
  try {
    const resp = await fetchIseMappingFields(endpoint, accountSettings.accountId)
    const keys = extractIseMappingFieldKeys(resp)
    availableFields.value = keys.length ? keys : [...(DISPLAY_FIELD_ORDER as unknown as string[])]
    
    // Use same configuration as AddVehicle - exclude vin and condition (shown separately)
    const excludedSet = new Set([...EXCLUDED_FIELDS, 'vin', 'condition'])
    displayFields.value = getDisplayFields(availableFields.value).filter(
      (field) => !excludedSet.has(field)
    )
  } catch (e) {
    console.error('Error fetching available fields', e)
    displayFields.value = []
  }
}

// Organize fields into visual sections (matching AddVehicle)
const fieldSections = computed(() => {
  const sections = [
    {
      label: 'Essential Information',
      fields: displayFields.value.filter((f) =>
        PRIORITY_FIELDS.essential.includes(f as (typeof PRIORITY_FIELDS.essential)[number]),
      ),
    },
    {
      label: 'Pricing & Mileage',
      fields: displayFields.value.filter((f) =>
        PRIORITY_FIELDS.pricing.includes(f as (typeof PRIORITY_FIELDS.pricing)[number]),
      ),
    },
    {
      label: 'Physical Characteristics',
      fields: displayFields.value.filter((f) =>
        PRIORITY_FIELDS.physical.includes(f as (typeof PRIORITY_FIELDS.physical)[number]),
      ),
    },
    {
      label: 'Specifications',
      fields: (() => {
        const fields = displayFields.value.filter((f) =>
          PRIORITY_FIELDS.specifications.includes(f as (typeof PRIORITY_FIELDS.specifications)[number]),
        )
        // tech_specs is excluded from the generic display list (complex structure),
        // but when present we show it as a dedicated full-width block under Specs.
        const techSpecs = (vehicle.value as Record<string, unknown> | null)?.tech_specs
        if (
          techSpecs != null &&
          techSpecs !== '' &&
          !fields.includes('tech_specs')
        ) {
          fields.push('tech_specs')
        }
        return fields
      })(),
    },
    {
      label: 'Description & Marketing',
      fields: displayFields.value.filter((f) =>
        PRIORITY_FIELDS.content.includes(f as (typeof PRIORITY_FIELDS.content)[number]),
      ),
    },
    {
      label: 'Certifications',
      fields: displayFields.value.filter((f) =>
        PRIORITY_FIELDS.certifications.includes(f as (typeof PRIORITY_FIELDS.certifications)[number]),
      ),
    },
    {
      label: 'Features',
      fields: displayFields.value.filter((f) =>
        PRIORITY_FIELDS.features.includes(f as (typeof PRIORITY_FIELDS.features)[number]),
      ),
    },
  ]

  // Filter out empty sections
  return sections.filter((section) => section.fields.length > 0)
})

const formatFieldLabel = (field: string): string =>
  field
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const formatFieldValue = (val: unknown, field?: string): string => {
  if (field) return formatVehicleFieldDisplayValue(field, val)
  // Fallback for callers that only pass a value.
  return formatVehicleFieldDisplayValue('', val)
}

const featureFieldSet = new Set<string>(PRIORITY_FIELDS.features)

const isFeatureField = (field: string): boolean => featureFieldSet.has(field)

const isStructuredSpecField = (field: string): boolean =>
  field === 'tech_specs' || isChromeTechSpecArray(getVehicleDetailFieldValue(field))

const isFullWidthDetailField = (sectionLabel: string, field: string): boolean =>
  sectionLabel === 'Description & Marketing' ||
  sectionLabel === 'Features' ||
  isFeatureField(field) ||
  field === 'tech_specs'

const formatFeatureFieldValue = (val: unknown): string => {
  if (val == null || val === '') return '—'
  if (Array.isArray(val)) return val.length > 0 ? val.map(String).join(', ') : '—'
  if (typeof val === 'string') {
    const items = val
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean)
    return items.length > 0 ? items.join(', ') : '—'
  }
  return formatFieldValue(val)
}

/**
 * Draft vehicles should not be pushed to OpenSearch directly.
 * Align with the existing "good" flow:
 * - remove any VIN visibility block rule (if present)
 * - mark dirty so the global "Publish Changes" flow handles processing/indexing
 * - keep publishState='draft' until publish pipeline completes
 */
const handleListVehicle = async () => {
  if (!accountSettings.accountId || !vin) return
  isPublishing.value = true
  try {
    // Load listing config so we can remove any VIN visibility block rule.
    await listingStore.getListingConfig()
    if (!listingStore.listingConfig) {
      throw new Error('No listing configuration available')
    }

    let removedRule = false
    if (hasManualVisibilityBlockRule.value) {
      const { config: nextConfig, removed } = removeVinVisibilityBlockRule(
        listingStore.listingConfig,
        vin,
      )
      removedRule = removed
      if (removedRule) {
        listingStore.listingConfig = nextConfig
      }

      const ok = await listingStore.saveListingConfig(listingStore.listingConfig)
      if (!ok) throw new Error('Failed to save publish rules')
    }

    // Note: We no longer mark inventory as "unpublished changes" since draft vehicles
    // are managed directly from the Inventory page and this Vehicle Details page.
    
    if (removedRule) {
      // Only mark listingRules dirty if there are *net* changes vs the published snapshot.
      // Otherwise, remove the unpublished marker for listingRules so the section doesn't show "0 rules".
      try {
        const { compareListingRules } = await import('@/utils/compareConfigs')
        const publishedConfig = (productSettings.publishedSnapshot as any)?.listingRules
        const currentConfig = listingStore.listingConfig
        const comparison = compareListingRules(publishedConfig, currentConfig as any)
        if (comparison?.hasChanges) {
          await publishStore.markDirty('inventoryMembership', {
            type: 'vinVisibilityBlockRuleRemoved',
            vin,
            timestamp: new Date().toISOString(),
            ...comparison,
          })
        } else {
          await productSettings.removeUnpublishedSource('listingRules')
        }
      } catch (e) {
        // Best-effort: if comparison fails, do not write a stub "listingRules" unpublished marker.
        // A stub marker causes "phantom" unpublished changes with 0 real diffs.
        console.warn('⚠️ Failed to diff publish rules after staging list (non-fatal):', e)
      }
    }

    // Refresh vehicle + listing status so the page doesn't show stale visibility reason/method
    // after the VIN visibility block rule is removed.
    try {
      const resp = await fetchVehicleByVin(endpoint, accountSettings.accountId, vin, true)
      websiteVisibility.value = resp.websiteVisibility ?? websiteVisibility.value
      websiteVisibilityReason.value =
        (resp.vehicle?.websiteVisibilityReason as string | undefined) ??
        resp.websiteVisibilityReason ??
        null
      publishState.value = resp.publishState ?? publishState.value
      await listingStore.getListingConfig()
      await fetchListedStatus()
      await fetchOpenSearchStatus()
    } catch (e) {
      // Non-fatal: UI will correct on refresh
      console.warn('⚠️ Failed to refetch vehicle data after staging list (non-fatal):', e)
    }

    snackbar.value = {
      show: true,
      message: 'Vehicle will be restored after you publish changes.',
      color: 'success',
    }
  } catch (error) {
    console.error('Error staging vehicle for listing:', error)
    snackbar.value = {
      show: true,
      message: error instanceof Error ? error.message : 'Failed to stage vehicle for restore.',
      color: 'error',
    }
  } finally {
    isPublishing.value = false
  }
}

const handleWebsiteVisibilityToggle = (newVal: boolean) => {
  if (!accountSettings.accountId || !vin) return
  if (!newVal) {
    // Hiding: show reason dialog before calling the API
    hideReason.value = null
    hideReasonDialog.value = true
    return
  }
  // Showing: fire immediately — no reason needed
  applyWebsiteVisibilityChange(true, undefined)
}

const cancelHideVehicle = () => {
  hideReasonDialog.value = false
  hideReason.value = null
}

const confirmHideVehicle = async () => {
  if (!hideReason.value) return
  hideReasonDialog.value = false
  await applyWebsiteVisibilityChange(false, hideReason.value)
  hideReason.value = null
}

const applyWebsiteVisibilityChange = async (newVal: boolean, reason?: string) => {
  if (!accountSettings.accountId || !vin) return
  isTogglingWebsiteVisibility.value = true
  try {
    const nextWebsiteVisibility = newVal ? WEBSITE_VISIBILITY.VISIBLE : WEBSITE_VISIBILITY.HIDDEN
    await toggleWebsiteVisibility(
      endpoint,
      accountSettings.accountId,
      vin,
      nextWebsiteVisibility,
      reason,
      undefined,
      WEBSITE_VISIBILITY_MODE.MANUAL,
      accountSettings.sessionToken,
    )
    websiteVisibility.value = nextWebsiteVisibility
    websiteVisibilityMode.value = WEBSITE_VISIBILITY_MODE.MANUAL
    snackbar.value = {
      show: true,
      message: newVal ? 'Vehicle is now visible on the website.' : 'Vehicle is now hidden from the website.',
      color: 'success',
    }
  } catch (err) {
    snackbar.value = {
      show: true,
      message: `Failed to update website visibility: ${err instanceof Error ? err.message : String(err)}`,
      color: 'error',
    }
  } finally {
    isTogglingWebsiteVisibility.value = false
  }
}

const handleReturnToAutomaticVisibility = async () => {
  if (!accountSettings.accountId || !vin) return
  isTogglingWebsiteVisibility.value = true
  try {
    const res = await returnWebsiteVisibilityToAuto(endpoint, accountSettings.accountId, vin, undefined, accountSettings.sessionToken)
    websiteVisibility.value = res.websiteVisibility ?? websiteVisibility.value
    websiteVisibilityMode.value = WEBSITE_VISIBILITY_MODE.AUTO
    websiteVisibilityReason.value = res.reason ?? null
    if (vehicle.value) (vehicle.value as any).websiteVisibilityExplanation = undefined
    snackbar.value = {
      show: true,
      message: 'Website visibility is now controlled automatically.',
      color: 'success',
    }
  } catch (err) {
    snackbar.value = {
      show: true,
      message: `Failed to return visibility to automatic: ${err instanceof Error ? err.message : String(err)}`,
      color: 'error',
    }
  } finally {
    isTogglingWebsiteVisibility.value = false
  }
}

const handleRemove = async () => {
  if (!accountSettings.accountId || !vin) return
  isRemoving.value = true
  try {
    // Best-effort cleanup: if this VIN was manually hidden via a VIN override rule,
    // remove that rule so it doesn't linger after the vehicle is archived.
    // This also ensures listingRules shows up in Unpublished Changes for publish.
    let removedRule = false
    try {
      await listingStore.getListingConfig()
      if (listingStore.listingConfig && hasManualVisibilityBlockRule.value) {
        const { config: nextConfig, removed } = removeVinVisibilityBlockRule(listingStore.listingConfig, vin)
        removedRule = removed
        if (removedRule) {
          listingStore.listingConfig = nextConfig
          const ok = await listingStore.saveListingConfig(listingStore.listingConfig)
          if (!ok) throw new Error('Failed to save publish rules')

          // Mark listingRules dirty only if there are net changes vs published snapshot.
          try {
            const { compareListingRules } = await import('@/utils/compareConfigs')
            const publishedConfig = (productSettings.publishedSnapshot as any)?.listingRules
            const currentConfig = listingStore.listingConfig
            const comparison = compareListingRules(publishedConfig, currentConfig as any)
            if (comparison?.hasChanges) {
              await publishStore.markDirty('inventoryMembership', {
                type: 'vinVisibilityBlockRuleRemovedOnRemove',
                vin,
                timestamp: new Date().toISOString(),
                ...comparison,
              })
            } else {
              await productSettings.removeUnpublishedSource('listingRules')
            }
          } catch (e) {
            console.warn('⚠️ Failed to diff publish rules after archive (non-fatal):', e)
          }
        }
      }
    } catch (e) {
      // Non-fatal: still allow staging archive; rule can be cleaned up manually.
      console.warn('⚠️ Failed to remove VIN visibility block rule while staging archive (non-fatal):', e)
    }

    // Feed-backed archive stages an operational PATCH (inventoryState=ARCHIVED) so it publishes
    // through the feed-backed operational-only archive path. Manual archive is unchanged and
    // continues to stage an intent=DELETE draft.
    if (isFeedBackedVehicle.value) {
      await stageVehicleArchiveOperationalPatch(
        endpoint,
        accountSettings.accountId,
        vin,
        accountSettings.sessionToken,
      )
      removeDialog.value = false
      // Reload so draftIntent/operationalPatch reflect the staged archive draft.
      await loadVehicleDetails()
    } else if (isManualVehicle.value) {
      await stageVehicleDeleteDraft(endpoint, accountSettings.accountId, vin, accountSettings.sessionToken)
      removeDialog.value = false
      draftIntent.value = 'DELETE'
    } else {
      throw new Error('This vehicle type cannot be archived from this action.')
    }

    snackbar.value = {
      show: true,
      message: 'Vehicle archive staged — publish changes to archive it from active inventory.',
      color: 'success',
    }
    // Refresh draft count so the left-nav "Unpublished Changes" link appears immediately.
    try {
      await publishStore.refreshUnpublishedChangesSummary()
    } catch (e) {
      // non-fatal
      console.warn('⚠️ Failed to refresh draft vehicle count after archive (non-fatal):', e)
    }
  } catch (e: unknown) {
    console.error('Failed to stage vehicle archive', e)
    const errMsg = e instanceof Error ? e.message : 'Failed to stage vehicle archive'
    snackbar.value = { show: true, message: errMsg, color: 'error' }
  } finally {
    isRemoving.value = false
  }
}

const handleDeleteCreateDraft = async () => {
  if (!accountSettings.accountId || !vin) return

  isDeletingDraft.value = true
  try {
    await revertVehicleDraft(endpoint, accountSettings.accountId, vin, accountSettings.sessionToken)

    snackbar.value = {
      show: true,
      message: 'Draft vehicle deleted.',
      color: 'success',
    }

    deleteDraftDialogOpen.value = false

    try {
      await publishStore.refreshUnpublishedChangesSummary()
    } catch (e) {
      console.warn('Failed to refresh unpublished summary after deleting draft:', e)
    }

    if (fromRoute.value === 'unpublished') {
      router.push({ name: 'UnpublishedChanges' })
    } else {
      router.push('/inventory')
    }
  } catch (error) {
    console.error('Failed to delete draft vehicle:', error)
    const message = error instanceof Error ? error.message : 'Unknown error'
    snackbar.value = {
      show: true,
      message: `Failed to delete draft vehicle: ${message}`,
      color: 'error',
    }
  } finally {
    isDeletingDraft.value = false
  }
}

const handleRevertStagedDelete = async () => {
  if (!accountSettings.accountId || !vin) return
  isCancelingDelete.value = true
  try {
    // Feed-backed archive is an operational PATCH draft: drop the draft row via revert.
    // Manual archive is an intent=DELETE draft: cancel via the delete-cancel endpoint (which
    // preserves any field edits on the draft), keeping manual behavior unchanged.
    if (isStagedForArchiveOperational.value) {
      await revertVehicleDraft(endpoint, accountSettings.accountId, vin, accountSettings.sessionToken)
    } else {
      await cancelVehicleDeleteDraft(endpoint, accountSettings.accountId, vin, accountSettings.sessionToken)
    }
    draftIntent.value = null
    snackbar.value = {
      show: true,
      message: 'Archive reverted.',
      color: 'success',
    }
    try {
      await publishStore.refreshUnpublishedChangesSummary()
    } catch (e) {
      console.warn('⚠️ Failed to refresh draft vehicle count after revert archive (non-fatal):', e)
    }
    // Reload to reflect draft archive cancellation (and any remaining field edits, if present)
    await loadVehicleDetails()
  } catch (e: unknown) {
    console.error('Failed to cancel staged archive', e)
    const errMsg = e instanceof Error ? e.message : 'Failed to cancel staged archive'
    snackbar.value = { show: true, message: errMsg, color: 'error' }
  } finally {
    isCancelingDelete.value = false
  }
}
</script>

<style scoped>
.status-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 11rem), 1fr));
  gap: 16px 24px;
}

.status-summary-item {
  min-width: 0;
}

.vehicle-details-photo-carousel :deep(.v-carousel__window),
.vehicle-details-photo-carousel :deep(.v-window__container),
.vehicle-details-photo-carousel :deep(.v-window-item) {
  height: auto !important;
}

.vehicle-details-photo-carousel-wrap {
  position: relative;
}

.vehicle-details-photo-carousel-fraction {
  position: absolute;
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%);
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 12px;
  line-height: 1.2;
  pointer-events: none;
}

.vehicle-details-photo-thumb {
  cursor: pointer;
}

.vehicle-details-photo-thumb:focus-visible {
  outline: 2px solid rgba(25, 118, 210, 0.9);
  outline-offset: 2px;
}

.vehicle-details-content-field {
  white-space: pre-wrap;
  word-break: break-word;
}

.vehicle-details-tech-specs {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  font-size: 0.8125rem;
  line-height: 1.45;
  max-height: 28rem;
  overflow: auto;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.04);
}

:global(.photo-preview-dialog) {
  width: 90vw;
  max-width: 90vw;
  height: 90vh;
  max-height: 90vh;
}

.photo-preview-card {
  height: 90vh;
  max-height: 90vh;
}

.photo-preview-body {
  height: 100%;
  padding: 0;
}

.photo-preview-wrap {
  position: relative;
  height: 100%;
}

.photo-preview-stage {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.photo-preview-fraction {
  position: absolute;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 12px;
  line-height: 1.2;
  pointer-events: none;
  z-index: 2;
}

.photo-preview-img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.photo-preview-close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
}

:global(.vehicle-history-dialog) {
  width: 90vw;
  max-width: 900px;
  height: 80vh;
  max-height: 80vh;
}

.vehicle-history-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.vehicle-history-body {
  flex: 1;
  overflow: auto;
}

.vehicle-details-card {
  position: relative;
}

.vehicle-history-corner {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
}

.vehicle-details-vin-row {
  width: 100%;
}

.vehicle-details-actions {
  flex: 1 1 auto;
  min-width: 0;
}

.vehicle-history-item {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/* Same horizontal padding as the expansion-panel title, so static rows and accordions align. */
.vehicle-history-item--static {
  padding: 12px 24px;
}

.vehicle-history-item__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
  width: 100%;
  min-width: 0;
}

/* The date and time never wrap (R17.5). */
.vehicle-history-item__date {
  white-space: nowrap;
}

.vehicle-history-item__sep {
  flex: none;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: rgba(var(--v-theme-on-surface), 0.38);
}

.vehicle-history-item__labeled {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

/* Labels (Operation, Source, Fields, Changes) never shrink or break. */
.vehicle-history-item__labeled > span:first-child {
  flex: none;
  white-space: nowrap;
}

/* Multi-line change lists keep their label on the first line. */
.vehicle-history-item__labeled--top {
  align-items: baseline;
}

.vehicle-history-item__source-chip {
  max-width: 100%;
  overflow-wrap: anywhere;
}

.vehicle-history-item__summary {
  flex: 1 1 240px;
  min-width: 0;
  overflow-wrap: anywhere;
}

/* An open accordion is shaded as a whole (title and body) with the light hover background. */
.vehicle-history-item :deep(.v-expansion-panel--active) {
  background-color: rgba(
    var(--v-theme-on-surface),
    calc(var(--v-hover-opacity) * var(--v-theme-overlay-multiplier))
  );
}

.vehicle-details-history-changes-table {
  background-color: transparent;
}

.vehicle-details-history-changes-table :deep(table) {
  table-layout: fixed;
  width: 100%;
}

.vehicle-details-history-changes-table td,
.vehicle-details-history-changes-table th {
  overflow-wrap: anywhere;
}

/* Long values (e.g. JSON, URLs) wrap and scroll inside a height-limited box (R17.3). */
.vehicle-details-history-value {
  max-height: 8rem;
  overflow-y: auto;
  overflow-wrap: anywhere;
}

.vehicle-details-history-lines {
  max-height: 10rem;
  overflow-y: auto;
}

/* One-line changes sit side by side; each change stays on one line. */
.vehicle-history-item__changes {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 8px;
}

.vehicle-history-item__change {
  white-space: nowrap;
}

/* Same colors as the Inventory visibility badges. */
.vehicle-history-item__visibility--visible {
  color: rgb(var(--v-theme-success));
  font-weight: 500;
}

.vehicle-history-item__visibility--hidden {
  color: rgb(var(--v-theme-warning));
  font-weight: 500;
}

.vehicle-details-history-changes-table th,
.vehicle-details-history-changes-table td {
  padding: 4px 8px;
}

.vehicle-details-history-correlation {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}
</style>
